import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { splitNumbers } from "src/utils/methods";

export type SummableColumnLike = {
  uuid: string;
  type: MetaFieldType;
  data?: { currencyCode?: string } | null;
};

type SummableMetaValue = {
  value: unknown;
  data?: { currencyCode?: string } | null;
};

export type SummableRowLike = {
  metadata?:
    | (SummableMetaValue & {
        uuid: string;
        overrideValue?: SummableMetaValue | null;
      })[]
    | null;
};

export type SelectedRowsTotal = {
  uuid: string;
  type: MetaFieldType;
  value: number;
  currencyCode?: string;
  formatted: string;
};

export type ComputeSelectedRowsTotalsOptions<Row extends SummableRowLike> = {
  getCurrencyCode?: (row: Row) => string | null | undefined;
};

const SUMMABLE_TYPES = [MetaFieldType.Currency, MetaFieldType.Number];

function toNumber(value: unknown): number | null {
  const num =
    typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isNaN(num) ? null : num;
}

function formatTotal(
  type: MetaFieldType,
  value: number,
  currencyCode?: string,
): string {
  const formattedValue = splitNumbers(value);
  if (type === MetaFieldType.Currency && currencyCode) {
    return `${currencyCode} ${formattedValue}`;
  }
  return formattedValue;
}


export function computeSelectedRowsTotals<Row extends SummableRowLike>(
  selectedRows: Row[],
  columns: SummableColumnLike[],
  options?: ComputeSelectedRowsTotalsOptions<Row>,
): SelectedRowsTotal[] {
  if (!selectedRows.length || !columns.length) return [];

  const summableColumnsByUuid = new Map(
    columns
      .filter((col) => SUMMABLE_TYPES.includes(col.type))
      .map((col) => [col.uuid, col]),
  );
  if (!summableColumnsByUuid.size) return [];

  const sums = new Map<
    string,
    { uuid: string; type: MetaFieldType; currencyCode?: string; value: number }
  >();

  for (const row of selectedRows) {
    for (const meta of row.metadata ?? []) {
      const col = summableColumnsByUuid.get(meta.uuid);
      if (!col) continue;

      const fieldData = meta.overrideValue || meta;

      const num = toNumber(fieldData.value);
      if (num === null) continue;

      const currencyCode =
        col.type === MetaFieldType.Currency
          ? fieldData.data?.currencyCode ??
            options?.getCurrencyCode?.(row) ??
            col.data?.currencyCode
          : undefined;

      const key = `${col.uuid}::${currencyCode ?? ""}`;
      const existing = sums.get(key);
      if (existing) {
        existing.value += num;
      } else {
        sums.set(key, {
          uuid: col.uuid,
          type: col.type,
          currencyCode,
          value: num,
        });
      }
    }
  }

  const totals: SelectedRowsTotal[] = [];
  for (const sum of sums.values()) {
    if (sum.value === 0) continue;
    const roundedValue = Math.round(sum.value * 100) / 100;
    totals.push({
      uuid: sum.uuid,
      type: sum.type,
      value: roundedValue,
      currencyCode: sum.currencyCode,
      formatted: formatTotal(sum.type, roundedValue, sum.currencyCode),
    });
  }

  return totals;
}

export type TotalRowRecord = Record<string, SelectedRowsTotal[]>;

export function buildTotalRowRecord(
  totals: SelectedRowsTotal[],
): TotalRowRecord {
  const record: TotalRowRecord = {};
  for (const total of totals) {
    (record[total.uuid] ??= []).push(total);
  }
  return record;
}
